"use client";

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import { useTranslations, useLocale } from "next-intl";
import { getPractices, Practice, getLocationAddress, getLocationCoords } from "@/data/practices";
import type { Translation } from "@/lib/api/client";

interface CitySuggestion {
    type: "city";
    name: string;
    lat: number;
    lng: number;
}

interface PracticeSuggestion {
    type: "practice";
    practice: Practice;
}

type SearchSuggestion = CitySuggestion | PracticeSuggestion;

/** Haversine distance in km between two coordinates */
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getTranslatedName(translations: Translation[], locale: string): string {
    const match = translations.find((t) => t.lang === locale);
    if (match) return match.value;
    const fallback = translations.find((t) => t.lang === "en");
    if (fallback) return fallback.value;
    return translations[0]?.value ?? "";
}

const PracticeMap = dynamic(() => import("@/components/PracticeMap"), {
    ssr: false,
    loading: () => (
        <div className="w-full h-full bg-[#28282e] animate-pulse flex items-center justify-center">
            <span className="text-[#78787E]">Loading map...</span>
        </div>
    ),
});

type FilterCategory = "all" | "medical" | "sports";

export default function MapViewPage() {
    const t = useTranslations();
    const locale = useLocale();
    const [practices, setPractices] = useState<Practice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [highlightedPracticeId, setHighlightedPracticeId] = useState<string | null>(null);
    const [selectedPractice, setSelectedPractice] = useState<Practice | null>(null);
    const [detailPractice, setDetailPractice] = useState<Practice | null>(null);
    const [sortMode, setSortMode] = useState<"none" | "az" | "za">("none");
    const [searchInput, setSearchInput] = useState("");
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
    const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const searchContainerRef = useRef<HTMLDivElement | null>(null);
    const searchContainerMobileRef = useRef<HTMLDivElement | null>(null);
    const userTypedRef = useRef(false);

    // Viewport mode state
    type SearchMode = "radius" | "viewport";
    const [searchMode, setSearchMode] = useState<SearchMode>("radius");
    const [viewportPractices, setViewportPractices] = useState<Practice[]>([]);
    const [isViewportLoading, setIsViewportLoading] = useState(false);
    const isProgrammaticMoveRef = useRef(true); // true initially to suppress FitBounds/FlyToUser
    const viewportDebounceRef = useRef<NodeJS.Timeout | null>(null);

    // Detect mobile (matches md: breakpoint at 768px)
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        setIsMobile(mq.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, []);

    // Mobile bottom sheet state
    type SheetSnap = "peek" | "half" | "full";
    const [sheetSnap, setSheetSnap] = useState<SheetSnap>("half");
    const sheetRef = useRef<HTMLDivElement>(null);
    const dragStartY = useRef(0);
    const dragStartHeight = useRef(0);
    const [sheetDragHeight, setSheetDragHeight] = useState<number | null>(null);
    const isDragging = useRef(false);

    const snapHeightVh: Record<SheetSnap, number> = {
        peek: 15,
        half: 45,
        full: 85,
    };

    const handleSheetTouchStart = useCallback((e: React.TouchEvent) => {
        isDragging.current = true;
        dragStartY.current = e.touches[0].clientY;
        dragStartHeight.current = sheetRef.current?.offsetHeight ?? (window.innerHeight * snapHeightVh[sheetSnap] / 100);
    }, [sheetSnap]);

    const handleSheetTouchMove = useCallback((e: React.TouchEvent) => {
        if (!isDragging.current) return;
        const deltaY = dragStartY.current - e.touches[0].clientY;
        const newHeight = Math.min(Math.max(dragStartHeight.current + deltaY, window.innerHeight * 0.1), window.innerHeight * 0.9);
        setSheetDragHeight(newHeight);
    }, []);

    const handleSheetTouchEnd = useCallback(() => {
        isDragging.current = false;
        if (sheetDragHeight === null) return;
        const vh = (sheetDragHeight / window.innerHeight) * 100;
        // Snap to closest position
        if (vh > 65) {
            setSheetSnap("full");
        } else if (vh > 25) {
            setSheetSnap("half");
        } else {
            setSheetSnap("peek");
        }
        setSheetDragHeight(null);
    }, [sheetDragHeight]);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [activeFilter, setActiveFilter] = useState<FilterCategory>("all");
    // Frankfurt am Main as default location
    const FRANKFURT = { lat: 50.1109, lng: 8.6821 };
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

    // Get browser geolocation and reverse geocode to city name
    useEffect(() => {
        const setFallback = () => {
            suppressViewportSwitch();
            setUserLocation(FRANKFURT);
            setSearchCenter(FRANKFURT);
            setSearchInput("Frankfurt am Main");
        };

        if (!navigator.geolocation) {
            setFallback();
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                suppressViewportSwitch();
                const { latitude, longitude } = position.coords;
                const loc = { lat: latitude, lng: longitude };
                setUserLocation(loc);
                setSearchCenter(loc);
                try {
                    const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=${locale}`,
                        { headers: { "User-Agent": "MyoactMap/1.0" } }
                    );
                    const data = await res.json();
                    const city = data.address?.city || data.address?.town || data.address?.village || data.address?.municipality || "";
                    if (city) {
                        setSearchInput(city);
                    }
                } catch {
                    // Reverse geocoding failed, just use location on map
                }
            },
            () => {
                setFallback();
            },
            { enableHighAccuracy: false, timeout: 10000 }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load practices from API
    useEffect(() => {
        async function loadPractices() {
            try {
                setIsLoading(true);
                setError(null);
                const data = await getPractices();
                setPractices(data);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load practices");
            } finally {
                setIsLoading(false);
            }
        }
        loadPractices();
    }, []);

    // Zoom control refs
    const zoomInRef = useRef<(() => void) | null>(null);
    const zoomOutRef = useRef<(() => void) | null>(null);
    const cardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const handleMapReady = useCallback((zoomIn: () => void, zoomOut: () => void) => {
        zoomInRef.current = zoomIn;
        zoomOutRef.current = zoomOut;
    }, []);

    // Suppress viewport mode switch during programmatic map movements
    const suppressViewportSwitch = useCallback(() => {
        isProgrammaticMoveRef.current = true;
        setTimeout(() => { isProgrammaticMoveRef.current = false; }, 1500);
    }, []);

    // Handle visible practices change from map viewport
    const handleVisiblePracticesChange = useCallback((practices: Practice[]) => {
        if (isProgrammaticMoveRef.current) return;
        setSearchMode("viewport");
        setIsViewportLoading(true);
        if (viewportDebounceRef.current) clearTimeout(viewportDebounceRef.current);
        viewportDebounceRef.current = setTimeout(() => {
            setViewportPractices(practices);
            setIsViewportLoading(false);
        }, 300);
    }, []);

    // Debounced search suggestions
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (!searchInput || searchInput.length < 2 || !userTypedRef.current) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            const query = searchInput.toLowerCase();
            const newSuggestions: SearchSuggestion[] = [];

            // Fetch city suggestions from Nominatim
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchInput)}&format=json&limit=5&addressdetails=1&accept-language=${locale}`,
                    { headers: { "User-Agent": "MyoactMap/1.0" } }
                );
                const cities = await res.json();
                for (const c of cities) {
                    const cityName = [
                        c.address?.city || c.address?.town || c.address?.village || c.name,
                        c.address?.state,
                        c.address?.country
                    ].filter(Boolean).join(", ");
                    newSuggestions.push({
                        type: "city",
                        name: cityName,
                        lat: parseFloat(c.lat),
                        lng: parseFloat(c.lon),
                    });
                }
            } catch {
                // Geocoding failed, continue with practice matches
            }

            // Add matching practices
            const matchingPractices = practices.filter((p) =>
                p.name.toLowerCase().includes(query) ||
                p.locations.some((loc) => getLocationAddress(loc).toLowerCase().includes(query))
            );
            for (const p of matchingPractices.slice(0, 5)) {
                newSuggestions.push({ type: "practice", practice: p });
            }

            setSuggestions(newSuggestions);
            setShowSuggestions(newSuggestions.length > 0);
        }, 300);

        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, [searchInput, practices]);

    // Close suggestions on outside click/touch
    useEffect(() => {
        const handleClick = (e: Event) => {
            const target = e.target as Node;
            const insideDesktop = searchContainerRef.current?.contains(target);
            const insideMobile = searchContainerMobileRef.current?.contains(target);
            if (!insideDesktop && !insideMobile) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("touchstart", handleClick);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("touchstart", handleClick);
        };
    }, []);

    const [flyToLocation, setFlyToLocation] = useState<{ lat: number; lng: number } | null>(null);

    const handleSelectCity = useCallback((city: CitySuggestion) => {
        suppressViewportSwitch();
        setSearchMode("radius");
        setViewportPractices([]);
        userTypedRef.current = false;
        setSearchInput(city.name);
        setSearchCenter({ lat: city.lat, lng: city.lng });
        setDetailPractice(null);
        setSelectedPractice(null);
        setHighlightedPracticeId(null);
        // Force new object reference to re-trigger fly
        setFlyToLocation({ lat: city.lat, lng: city.lng });
        setShowSuggestions(false);
        setSheetSnap("half");
    }, [suppressViewportSwitch]);

    const handleClearSearch = useCallback(() => {
        userTypedRef.current = false;
        setSearchInput("");
        setSearchCenter(null);
        setSuggestions([]);
        setShowSuggestions(false);
        setSearchMode("radius");
        setViewportPractices([]);
    }, []);

    const SEARCH_RADIUS_KM = 20;

    const displayedPractices = useMemo(() => {
        let result: Practice[];

        if (searchMode === "viewport") {
            // Use practices visible in the current map viewport
            result = viewportPractices;
        } else {
            // Filter by city radius
            result = practices;
            if (searchCenter) {
                result = result.filter((p) =>
                    p.locations.some((loc) => {
                        const coords = getLocationCoords(loc);
                        if (!coords.lat && !coords.lng) return false;
                        return haversineKm(searchCenter.lat, searchCenter.lng, coords.lat, coords.lng) <= SEARCH_RADIUS_KM;
                    })
                );
            }
        }
        // Filter by context (applies in both modes)
        if (activeFilter !== "all") {
            result = result.filter((p) =>
                p.context?.toLowerCase().includes(activeFilter)
            );
        }
        if (sortMode === "az") {
            return [...result].sort((a, b) => a.name.localeCompare(b.name, locale, { sensitivity: "base" }));
        }
        if (sortMode === "za") {
            return [...result].sort((a, b) => b.name.localeCompare(a.name, locale, { sensitivity: "base" }));
        }
        return result;
    }, [practices, sortMode, searchCenter, activeFilter, locale, searchMode, viewportPractices]);

    const handlePracticeClick = useCallback((practice: Practice) => {
        setDetailPractice(practice);
        setHighlightedPracticeId(practice.id);
        // Fly to on map
        setSelectedPractice(null);
        setTimeout(() => setSelectedPractice(practice), 0);
    }, []);

    const handleSelectPractice = useCallback((practice: Practice) => {
        suppressViewportSwitch();
        userTypedRef.current = false;
        setSearchInput(practice.name);
        setShowSuggestions(false);
        setSearchCenter(null);
        handlePracticeClick(practice);
        setSheetSnap("half");
    }, [handlePracticeClick, suppressViewportSwitch]);

    const handleMarkerClick = useCallback((practiceId: string | null) => {
        if (!practiceId) return;
        suppressViewportSwitch();
        const practice = practices.find((p) => p.id === practiceId);
        if (practice) {
            setDetailPractice(practice);
            setHighlightedPracticeId(practiceId);
            // Fly to pin centered in visible area
            setSelectedPractice(null);
            setTimeout(() => setSelectedPractice(practice), 0);
            // Scroll list to the selected card
            cardRefs.current[practiceId]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
            // Expand mobile bottom sheet
            setSheetSnap("half");
        }
    }, [practices, suppressViewportSwitch]);

    const handleCloseDetail = useCallback(() => {
        setDetailPractice(null);
        setHighlightedPracticeId(null);
    }, []);

    // Loading state
    if (isLoading) {
        return (
            <div className="h-[calc(100vh-50px)] md:h-[calc(100vh-66px)] flex items-center justify-center bg-bg-primary">
                <div className="text-center">
                    <div className="animate-spin w-12 h-12 border-4 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-text-secondary">{t("status.loadingPractices")}</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="h-[calc(100vh-50px)] md:h-[calc(100vh-66px)] flex items-center justify-center bg-bg-primary">
                <div className="card p-12 text-center max-w-md">
                    <div className="text-5xl mb-4">⚠️</div>
                    <h2 className="text-xl font-semibold text-text-primary mb-2">
                        {t("status.errorLoadingPractices")}
                    </h2>
                    <p className="text-text-secondary mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="btn-primary"
                    >
                        {t("buttons.tryAgain")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-50px)] md:h-[calc(100vh-66px)] relative overflow-hidden">
            {/* Map - Full size base layer */}
            <div className="absolute inset-0">
                <PracticeMap
                    practices={practices}
                    filteredPracticeIds={displayedPractices.map(p => p.id)}
                    highlightedPracticeId={highlightedPracticeId}
                    onPracticeHover={handleMarkerClick}
                    onVisiblePracticesChange={handleVisiblePracticesChange}
                    selectedPractice={selectedPractice}
                    userLocation={userLocation}
                    invalidateSize={true}
                    onMapReady={handleMapReady}
                    flyToOffsetX={isMobile ? 0 : sidebarOpen ? (detailPractice ? 780 : 380) : 0}
                    flyToOffsetY={isMobile ? window.innerHeight * snapHeightVh[sheetSnap] / 100 : 0}
                    flyToLocation={flyToLocation}
                />
            </div>

            {/* Overlay: Left Sidebar */}
            <div className={`hidden md:flex items-center absolute left-0 top-0 h-full z-[500] transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-[380px]'}`}>
                <div className="bg-bg-card flex h-full items-start overflow-hidden p-4 flex-shrink-0">
                    <div className="flex flex-col gap-1.5 items-start w-[350px]">
                        {/* Search Bar with Suggestions */}
                        <div className="relative w-full" ref={searchContainerRef}>
                            <div className="input-search w-full">
                                <input
                                    type="text"
                                    placeholder={t("placeholders.searchPractices")}
                                    value={searchInput}
                                    onChange={(e) => { userTypedRef.current = true; setSearchInput(e.target.value); }}
                                    onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                                    className="flex-1 bg-transparent text-white outline-none text-sm"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                />
                                <div className="flex items-center">
                                    <button
                                        className="flex items-center justify-center w-8 h-8 rounded-full text-text-secondary hover:text-white transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </button>
                                    {searchInput && (
                                        <button
                                            onClick={handleClearSearch}
                                            className="flex items-center justify-center w-8 h-8 rounded-full text-text-secondary hover:text-white transition-colors"
                                            aria-label="Clear search"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Suggestions Dropdown */}
                            {showSuggestions && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-bg-card border border-[var(--border-default)] rounded-xl overflow-hidden z-[700] max-h-[300px] overflow-y-auto">
                                    {suggestions.filter(s => s.type === "city").length > 0 && (
                                        <>
                                            <p className="px-3 py-1.5 text-[10px] text-text-secondary uppercase tracking-wide">{t("labels.cities")}</p>
                                            {suggestions.filter(s => s.type === "city").map((s, i) => {
                                                const city = s as CitySuggestion;
                                                return (
                                                    <button
                                                        key={`city-${i}`}
                                                        onClick={() => handleSelectCity(city)}
                                                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[var(--border-default)] transition-colors flex items-center gap-2"
                                                    >
                                                        <svg className="w-4 h-4 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        </svg>
                                                        {city.name}
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                    {suggestions.filter(s => s.type === "practice").length > 0 && (
                                        <>
                                            <p className="px-3 py-1.5 text-[10px] text-text-secondary uppercase tracking-wide">{t("labels.practices")}</p>
                                            {suggestions.filter(s => s.type === "practice").map((s, i) => {
                                                const ps = s as PracticeSuggestion;
                                                return (
                                                    <button
                                                        key={`practice-${i}`}
                                                        onClick={() => handleSelectPractice(ps.practice)}
                                                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[var(--border-default)] transition-colors flex items-center gap-2"
                                                    >
                                                        <svg className="w-4 h-4 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                        </svg>
                                                        {ps.practice.name}
                                                    </button>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Title Row: Results count + Sort */}
                        <div className="flex items-center justify-between w-full">
                            <p className="flex-1 text-xs text-white leading-4">
                                {t("labels.resultsCount", { count: displayedPractices.length })}
                            </p>
                            <button
                                onClick={() => setSortMode((prev) => prev === "none" ? "az" : prev === "az" ? "za" : "none")}
                                className={`flex items-center gap-1.5 h-8 px-3 rounded cursor-pointer ${sortMode !== "none" ? 'text-accent' : 'text-white'}`}
                            >
                                {sortMode !== "none" && (
                                    <svg className={`w-3 h-3 transition-transform ${sortMode === "az" ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6.5925 1V12.17L1.7125 7.29C1.3225 6.9 0.6825 6.9 0.2925 7.29C-0.0975 7.68 -0.0975 8.31 0.2925 8.7L6.8825 15.29C7.2725 15.68 7.9025 15.68 8.2925 15.29L14.8825 8.7C15.2725 8.31 15.2725 7.68 14.8825 7.29C14.4925 6.9 13.8625 6.9 13.4725 7.29L8.5925 12.17V1C8.5925 0.45 8.1425 0 7.5925 0C7.0425 0 6.5925 0.45 6.5925 1Z" fill="currentColor" />
                                    </svg>
                                )}
                                <span className="font-bold text-[10px] uppercase tracking-wide">
                                    {t("buttons.sortAZ")}
                                </span>
                            </button>
                        </div>

                        {/* Practice Cards List */}
                        <div className="relative flex flex-col gap-2 items-start pb-24 w-full overflow-y-auto max-h-[calc(100vh-230px)] pr-2">
                            {isViewportLoading && (
                                <div className="absolute inset-0 bg-bg-card/60 z-10 flex items-start justify-center pt-8">
                                    <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}
                            {displayedPractices.length > 0 ? (
                                displayedPractices.map((practice) => (
                                    <div
                                        key={practice.id}
                                        ref={(el) => { cardRefs.current[practice.id] = el; }}
                                        className={`card w-full cursor-pointer transition-all ${highlightedPracticeId === practice.id ? "card-highlighted" : ""}`}
                                        onClick={() => handlePracticeClick(practice)}
                                        onMouseEnter={() => setHighlightedPracticeId(practice.id)}
                                        onMouseLeave={() => {
                                            if (detailPractice?.id !== practice.id) {
                                                setHighlightedPracticeId(detailPractice?.id ?? null);
                                            }
                                        }}
                                    >
                                        {/* Company Info Row */}
                                        <div className="flex gap-3 items-center">
                                            <div className={practice.logoUrl ? "logo-placeholder" : ""} style={{ width: '40px', height: '40px' }}>
                                                <img
                                                    src={practice.logoUrl || "/default-avatar.svg"}
                                                    alt={practice.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 flex flex-col justify-center">
                                                <h3 className="text-[17px] font-bold text-white leading-5">
                                                    {practice.name}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Specialization + Address */}
                                        <div className="flex flex-col gap-1.5 mt-1.5">
                                            {practice.specialization && (
                                                <p className="text-xs text-white leading-4 tracking-tight">
                                                    {practice.specialization}
                                                </p>
                                            )}
                                            {practice.locations[0] && (
                                                <div className="flex gap-1.5 items-center">
                                                    <svg className="w-4 h-4 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    <span className="text-xs text-text-secondary leading-4 tracking-tight">
                                                        {getLocationAddress(practice.locations[0])}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-3 mt-1.5 items-center justify-center">
                                            {practice.locations[0] && (
                                                <a
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${getLocationCoords(practice.locations[0]).lat},${getLocationCoords(practice.locations[0]).lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-ghost py-1.5 rounded-xl text-center text-xs text-accent"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M21.41 10.59l-7.99-8c-.78-.78-2.05-.78-2.83 0l-8 8c-.78.78-.78 2.05 0 2.83l8 8c.78.78 2.05.78 2.83 0l7.99-8c.79-.79.79-2.05 0-2.83zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z" />
                                                    </svg>
                                                    {t("buttons.getDirections")}
                                                </a>
                                            )}
                                            {practice.locations[0]?.siteUrl && (
                                                <a
                                                    href={practice.locations[0].siteUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-secondary text-center text-xs"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {t("buttons.openWebsite")}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-12 empty-state w-full">
                                    <p>{t("status.noPracticesVisible")}</p>
                                    <p className="text-sm mt-1">{t("status.zoomOutOrAdjust")}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sidebar Toggle Button */}
                <button
                    onClick={() => setSidebarOpen((prev) => !prev)}
                    className="sidebar-toggle flex-shrink-0"
                    aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                >
                    <svg className={`w-4 h-4 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            </div>

            {/* Overlay: Floating Detail Card + Filter Toolbar column */}
            <div className={`hidden md:flex absolute top-4 z-[600] flex-col gap-2.5 items-start pl-4 transition-all duration-300 ${sidebarOpen ? 'left-[394px]' : 'left-6'}`}>
                {/* Filter Toolbar */}
                <div className="filter-toolbar">
                    {(["all", "medical", "sports"] as FilterCategory[]).map((category) => (
                        <button
                            key={category}
                            onClick={() => setActiveFilter(category)}
                            className={`filter-chip ${activeFilter === category ? 'filter-chip-active' : ''}`}
                        >
                            {t(`filters.${category}`)}
                        </button>
                    ))}
                </div>

                {/* Floating Detail Card - appears when a practice is selected */}
                {detailPractice && (
                    <div className="bg-bg-card rounded-[24px] w-[320px] max-h-[calc(100vh-200px)] overflow-y-auto animate-in">
                        <div className="flex flex-col gap-3 p-5">
                            {/* Header: Logo + Name + Close */}
                            <div className="flex gap-2 items-start">
                                <div className="flex-1 flex gap-3 items-center">
                                    <div className={detailPractice.logoUrl ? "logo-placeholder flex-shrink-0" : "flex-shrink-0"} style={{ width: '44px', height: '44px', borderRadius: '6px' }}>
                                        <img src={detailPractice.logoUrl || "/default-avatar.svg"} alt={detailPractice.name} className="w-full h-full object-cover" />
                                    </div>
                                    <h2 className="flex-1 text-[17px] font-bold text-white leading-tight">
                                        {detailPractice.name}
                                    </h2>
                                </div>
                                <button
                                    onClick={handleCloseDetail}
                                    className="flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:text-white transition-colors flex-shrink-0"
                                    aria-label="Close details"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Specialization */}
                            {detailPractice.specialization && (
                                <div className="pb-1">
                                    <p className="text-xs text-white leading-4 tracking-tight">
                                        {detailPractice.specialization}
                                    </p>
                                </div>
                            )}

                            {/* Contact Info: Address, Phone, Email */}
                            <div className="border-t border-b border-[var(--border-default)] flex flex-col gap-2 py-2.5">
                                {detailPractice.locations[0] && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="contact-text text-xs">{getLocationAddress(detailPractice.locations[0])}</span>
                                    </div>
                                )}
                                {(detailPractice.phone || detailPractice.locations[0]?.phone) && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href={`tel:${detailPractice.phone || detailPractice.locations[0]?.phone}`} className="contact-text text-xs hover:text-accent transition-colors">
                                            {detailPractice.phone || detailPractice.locations[0]?.phone}
                                        </a>
                                    </div>
                                )}
                                {(detailPractice.email || detailPractice.locations[0]?.email) && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <a href={`mailto:${detailPractice.email || detailPractice.locations[0]?.email}`} className="contact-text text-xs hover:text-accent transition-colors">
                                            {detailPractice.email || detailPractice.locations[0]?.email}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Services */}
                            {detailPractice.services.length > 0 && (
                                <div className="border-b border-[var(--border-default)] flex flex-col gap-2 pb-2.5">
                                    <p className="text-xs text-white leading-4">{t("labels.services")}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detailPractice.services.map((service) => (
                                            <span key={service.id} className="chip text-xs py-1 px-2.5">
                                                {getTranslatedName(service.name, locale)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 items-center">
                                {detailPractice.locations[0] && (
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&destination=${getLocationCoords(detailPractice.locations[0]).lat},${getLocationCoords(detailPractice.locations[0]).lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-ghost flex-1 py-2 text-xs text-center text-accent"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M21.41 10.59l-7.99-8c-.78-.78-2.05-.78-2.83 0l-8 8c-.78.78-.78 2.05 0 2.83l8 8c.78.78 2.05.78 2.83 0l7.99-8c.79-.79.79-2.05 0-2.83zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z" />
                                        </svg>
                                        {t("buttons.getDirections")}
                                    </a>
                                )}
                                {detailPractice.locations[0]?.siteUrl && (
                                    <a
                                        href={detailPractice.locations[0].siteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-secondary flex-1 text-xs text-center"
                                    >
                                        {t("buttons.openWebsite")}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Bottom Sheet */}
            <div
                ref={sheetRef}
                className={`md:hidden absolute left-0 right-0 z-[600] bg-bg-card rounded-t-[20px] flex flex-col overscroll-none ${sheetDragHeight === null ? 'transition-[height] duration-300 ease-out' : ''}`}
                style={{ bottom: '28px', height: sheetDragHeight !== null ? `${sheetDragHeight}px` : `${snapHeightVh[sheetSnap]}vh` }}
            >
                {/* Drag Handle */}
                <div
                    className="flex justify-center py-2.5 cursor-grab touch-none flex-shrink-0"
                    onTouchStart={handleSheetTouchStart}
                    onTouchMove={handleSheetTouchMove}
                    onTouchEnd={handleSheetTouchEnd}
                >
                    <div className="w-10 h-1 rounded-full bg-[#555]" />
                </div>

                {detailPractice ? (
                    /* Detail View */
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                        <div className="flex flex-col gap-3">
                            {/* Header: Logo + Name + Close */}
                            <div className="flex gap-2 items-start">
                                <div className="flex-1 flex gap-3 items-center">
                                    <div className={detailPractice.logoUrl ? "logo-placeholder flex-shrink-0" : "flex-shrink-0"} style={{ width: '44px', height: '44px', borderRadius: '6px' }}>
                                        <img src={detailPractice.logoUrl || "/default-avatar.svg"} alt={detailPractice.name} className="w-full h-full object-cover" />
                                    </div>
                                    <h2 className="flex-1 text-[17px] font-bold text-white leading-tight">
                                        {detailPractice.name}
                                    </h2>
                                </div>
                                <button
                                    onClick={handleCloseDetail}
                                    className="flex items-center justify-center w-9 h-9 rounded-full text-text-secondary hover:text-white transition-colors flex-shrink-0"
                                    aria-label="Close details"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {detailPractice.specialization && (
                                <p className="text-xs text-white leading-4 tracking-tight">
                                    {detailPractice.specialization}
                                </p>
                            )}

                            {/* Action Buttons */}
                            <div className="flex gap-3 items-center border-t border-[var(--border-default)] pt-3">
                                {detailPractice.locations[0] && (
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&destination=${getLocationCoords(detailPractice.locations[0]).lat},${getLocationCoords(detailPractice.locations[0]).lng}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-ghost flex-1 py-2 text-xs text-center text-accent"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M21.41 10.59l-7.99-8c-.78-.78-2.05-.78-2.83 0l-8 8c-.78.78-.78 2.05 0 2.83l8 8c.78.78 2.05.78 2.83 0l7.99-8c.79-.79.79-2.05 0-2.83zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z" />
                                        </svg>
                                        {t("buttons.getDirections")}
                                    </a>
                                )}
                                {detailPractice.locations[0]?.siteUrl && (
                                    <a
                                        href={detailPractice.locations[0].siteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn-secondary flex-1 text-xs text-center"
                                    >
                                        {t("buttons.openWebsite")}
                                    </a>
                                )}
                            </div>

                            {/* Contact Info */}
                            <div className="border-t border-b border-[var(--border-default)] flex flex-col gap-2 py-2.5">
                                {detailPractice.locations[0] && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className="contact-text text-xs">{getLocationAddress(detailPractice.locations[0])}</span>
                                    </div>
                                )}
                                {(detailPractice.phone || detailPractice.locations[0]?.phone) && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href={`tel:${detailPractice.phone || detailPractice.locations[0]?.phone}`} className="contact-text text-xs hover:text-accent transition-colors">
                                            {detailPractice.phone || detailPractice.locations[0]?.phone}
                                        </a>
                                    </div>
                                )}
                                {(detailPractice.email || detailPractice.locations[0]?.email) && (
                                    <div className="contact-row">
                                        <svg className="contact-icon flex-shrink-0 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <a href={`mailto:${detailPractice.email || detailPractice.locations[0]?.email}`} className="contact-text text-xs hover:text-accent transition-colors">
                                            {detailPractice.email || detailPractice.locations[0]?.email}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Services */}
                            {detailPractice.services.length > 0 && (
                                <div className="flex flex-col gap-2 pb-2.5">
                                    <p className="text-xs text-white leading-4">{t("labels.services")}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {detailPractice.services.map((service) => (
                                            <span key={service.id} className="chip text-xs py-1 px-2.5">
                                                {getTranslatedName(service.name, locale)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>
                ) : (
                    /* List View */
                    <div className={`flex-1 px-4 pb-4 flex flex-col gap-2 ${showSuggestions ? 'overflow-visible' : 'overflow-y-auto'}`}>
                        {/* Search Bar + Filters (sticky) */}
                        <div className="sticky top-0 z-10 bg-bg-card flex flex-col gap-2 pb-1 flex-shrink-0">
                            <div className="relative" ref={searchContainerMobileRef}>
                                <div className="input-search w-full">
                                    <input
                                        type="text"
                                        placeholder={t("placeholders.searchPractices")}
                                        value={searchInput}
                                        onChange={(e) => { userTypedRef.current = true; setSearchInput(e.target.value); }}
                                        onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); setSheetSnap("full"); }}
                                        className="flex-1 bg-transparent text-white outline-none text-sm"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                    />
                                    <div className="flex items-center">
                                        <button className="flex items-center justify-center w-8 h-8 rounded-full text-text-secondary hover:text-white transition-colors">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </button>
                                        {searchInput && (
                                            <button
                                                onClick={handleClearSearch}
                                                className="flex items-center justify-center w-8 h-8 rounded-full text-text-secondary hover:text-white transition-colors"
                                                aria-label="Clear search"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Suggestions Dropdown */}
                                {showSuggestions && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-bg-card border border-[var(--border-default)] rounded-xl overflow-hidden z-[700] max-h-[200px] overflow-y-auto">
                                        {suggestions.filter(s => s.type === "city").length > 0 && (
                                            <>
                                                <p className="px-3 py-1.5 text-[10px] text-text-secondary uppercase tracking-wide">{t("labels.cities")}</p>
                                                {suggestions.filter(s => s.type === "city").map((s, i) => {
                                                    const city = s as CitySuggestion;
                                                    return (
                                                        <button
                                                            key={`city-m-${i}`}
                                                            onClick={() => handleSelectCity(city)}
                                                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[var(--border-default)] transition-colors flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                            </svg>
                                                            {city.name}
                                                        </button>
                                                    );
                                                })}
                                            </>
                                        )}
                                        {suggestions.filter(s => s.type === "practice").length > 0 && (
                                            <>
                                                <p className="px-3 py-1.5 text-[10px] text-text-secondary uppercase tracking-wide">{t("labels.practices")}</p>
                                                {suggestions.filter(s => s.type === "practice").map((s, i) => {
                                                    const ps = s as PracticeSuggestion;
                                                    return (
                                                        <button
                                                            key={`practice-m-${i}`}
                                                            onClick={() => handleSelectPractice(ps.practice)}
                                                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[var(--border-default)] transition-colors flex items-center gap-2"
                                                        >
                                                            <svg className="w-4 h-4 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                            </svg>
                                                            {ps.practice.name}
                                                        </button>
                                                    );
                                                })}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Filter Chips + Sort */}
                            <div className="flex gap-2 flex-shrink-0 items-center">
                                {(["all", "medical", "sports"] as FilterCategory[]).map((category) => (
                                    <button
                                        key={category}
                                        onClick={() => setActiveFilter(category)}
                                        className={`filter-chip ${activeFilter === category ? 'filter-chip-active' : ''}`}
                                    >
                                        {t(`filters.${category}`)}
                                    </button>
                                ))}
                                <button
                                    onClick={() => setSortMode((prev) => prev === "none" ? "az" : prev === "az" ? "za" : "none")}
                                    className={`flex items-center gap-1 h-8 px-2 rounded cursor-pointer ml-auto ${sortMode !== "none" ? 'text-accent' : 'text-white'}`}
                                >
                                    {sortMode !== "none" && (
                                        <svg className={`w-3 h-3 transition-transform ${sortMode === "az" ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6.5925 1V12.17L1.7125 7.29C1.3225 6.9 0.6825 6.9 0.2925 7.29C-0.0975 7.68 -0.0975 8.31 0.2925 8.7L6.8825 15.29C7.2725 15.68 7.9025 15.68 8.2925 15.29L14.8825 8.7C15.2725 8.31 15.2725 7.68 14.8825 7.29C14.4925 6.9 13.8625 6.9 13.4725 7.29L8.5925 12.17V1C8.5925 0.45 8.1425 0 7.5925 0C7.0425 0 6.5925 0.45 6.5925 1Z" fill="currentColor" />
                                        </svg>
                                    )}
                                    <span className="font-bold text-[10px] uppercase tracking-wide">
                                        {t("buttons.sortAZ")}
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Results count */}
                        <p className="text-xs text-text-secondary flex-shrink-0">
                            {t("labels.resultsCount", { count: displayedPractices.length })}
                        </p>

                        {/* Practice Cards */}
                        <div className="relative w-full flex flex-col gap-2">
                            {isViewportLoading && (
                                <div className="absolute inset-0 bg-bg-card/60 z-10 flex items-start justify-center pt-8">
                                    <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                                </div>
                            )}
                            {displayedPractices.length > 0 ? (
                                displayedPractices.map((practice) => (
                                    <div
                                        key={practice.id}
                                        className={`card w-full cursor-pointer transition-all ${highlightedPracticeId === practice.id ? "card-highlighted" : ""}`}
                                        onClick={() => { handlePracticeClick(practice); setSheetSnap("half"); }}
                                    >
                                        <div className="flex gap-3 items-center">
                                            <div className={practice.logoUrl ? "logo-placeholder" : ""} style={{ width: '40px', height: '40px' }}>
                                                <img src={practice.logoUrl || "/default-avatar.svg"} alt={practice.name} className="w-full h-full object-cover" />
                                            </div>
                                            <div className="flex-1 flex flex-col justify-center">
                                                <h3 className="text-[15px] font-bold text-white leading-5">{practice.name}</h3>
                                                {practice.locations[0] && (
                                                    <span className="text-xs text-text-secondary leading-4 mt-0.5">
                                                        {getLocationAddress(practice.locations[0])}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {practice.specialization && (
                                            <p className="text-xs text-white leading-4 tracking-tight mt-1.5">
                                                {practice.specialization}
                                            </p>
                                        )}

                                        <div className="flex gap-3 mt-1.5 items-center justify-center">
                                            {practice.locations[0] && (
                                                <a
                                                    href={`https://www.google.com/maps/dir/?api=1&destination=${getLocationCoords(practice.locations[0]).lat},${getLocationCoords(practice.locations[0]).lng}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-ghost py-1.5 rounded-xl text-center text-xs text-accent"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M21.41 10.59l-7.99-8c-.78-.78-2.05-.78-2.83 0l-8 8c-.78.78-.78 2.05 0 2.83l8 8c.78.78 2.05.78 2.83 0l7.99-8c.79-.79.79-2.05 0-2.83zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z" />
                                                    </svg>
                                                    {t("buttons.getDirections")}
                                                </a>
                                            )}
                                            {practice.locations[0]?.siteUrl && (
                                                <a
                                                    href={practice.locations[0].siteUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn-secondary text-center text-xs"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {t("buttons.openWebsite")}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8 empty-state w-full">
                                    <p>{t("status.noPracticesVisible")}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Overlay: Map Zoom Controls */}
            <div className={`absolute right-4 z-[500] flex flex-col gap-2 transition-all duration-300 ${sheetSnap === "full" ? "bottom-[89vh]" : sheetSnap === "half" ? "bottom-[49vh]" : "bottom-[19vh]"} md:bottom-14 md:right-6`}>
                <button
                    onClick={() => zoomInRef.current?.()}
                    className="map-control"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v12M6 12h12" />
                    </svg>
                </button>
                <button
                    onClick={() => zoomOutRef.current?.()}
                    className="map-control"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4" />
                    </svg>
                </button>
            </div>

            {/* Footer */}
            <div className="absolute bottom-0 left-0 right-0 z-[700] bg-bg-card border-t border-[var(--border-default)] py-2 text-center text-[10px] text-text-secondary">
                &copy; {new Date().getFullYear()} myoact. All rights reserved. (0.2.8)
            </div>
        </div>
    );
}
